/* eslint-disable @next/next/no-img-element */
import { ReactElement, useState } from 'react';
import useTranslation from 'shared/hooks/useTranslation';
import styled, { css } from 'styled-components';
import { scrollBar } from 'shared/components/mixins';
import { DesktopBlur, MobileBlur, Wrapper } from './styled';
import BlurElement from './BlurElement';

function Departments(): JSX.Element {
  const { t } = useTranslation('ourTeamPage');

  const [activeTab, setActiveTab] = useState('Sales');

  type Department = {
    src: string;
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    description: ReactElement<any, any>;
  };

  type Departments = {
    [key: string]: Department;
  };

  const DEPARTMENTS: Departments = {
    Sales: {
      src: '/img/our-team/sales.png',
      name: 'Sales',
      description: (
        <>
          <p>{t('strongTeam')}</p>
          <p>{t('deepExpertise')}</p>
          <p>
            <span>{t('impressivePortfolio')}</span>
          </p>
        </>
      ),
    },
    'Customer Success': {
      src: '/img/our-team/customer-service.png',
      name: 'Customer Success',
      description: (
        <>
          <p>{t('specialTasksTeam')} 😉</p>
          <p>{t('customerSupportTeam')}</p>
          <p>
            <span>{t('helpfulTeam')}</span>
          </p>
        </>
      ),
    },
    IT: {
      src: '/img/our-team/it.png',
      name: 'IT',
      description: (
        <>
          <p>{t('hiddenHeroes')}</p>
          <p>
            <span>{t('itTeam')}</span>
          </p>
        </>
      ),
    },
    Marketing: {
      src: '/img/our-team/marketing.png',
      name: 'Marketing',
      description: (
        <>
          <p>{t('imageTeam')}</p>
          <p>{t('communicationTeam')}</p>
          <p>
            <span>{t('socialMediaTeam')}</span>
          </p>
        </>
      ),
    },
    'TrustMate Hospitality': {
      src: '/img/our-team/trustmate-hospitality.png',
      name: 'TrustMate Hospitality',
      description: (
        <>
          <p>{t('horecaTeam')}</p>
          <p>
            <span>{t('challenges')}</span>
          </p>
        </>
      ),
    },
    Backoffice: {
      src: '/img/our-team/backoffice.png',
      name: 'Backoffice',
      description: (
        <>
          <p>{t('backofficeTeam')}</p>
          <p>
            <span>{t('backofficeSupport')}</span>
          </p>
        </>
      ),
    },
  };

  return (
    <>
      <DesktopBlur>
        <BlurElement speed={0.1} size={800} position={{ x: 40, y: -400 }} />
        <BlurElement speed={0.1} size={400} position={{ x: 0, y: -600 }} />
        <BlurElement speed={0.1} size={800} direction="right" position={{ x: 0, y: -700 }} />
        <BlurElement speed={0.1} size={400} direction="right" position={{ x: 80, y: -600 }} />
      </DesktopBlur>
      <MobileBlur>
        <BlurElement speed={0.5} size={300} position={{ x: 0, y: -200 }} />
        <BlurElement speed={0.5} size={300} direction="right" position={{ x: 0, y: -100 }} />
        <BlurElement speed={0.4} size={300} position={{ x: 0, y: -1100 }} />
      </MobileBlur>
      <Wrapper style={{ overflowX: 'hidden' }} maxWidth>
        <TabsWrapper>
          <ActiveTab>{activeTab}</ActiveTab>
          <Tabs>
            {Object.keys(DEPARTMENTS).map((keyName) => (
              <Tab isActive={keyName === activeTab} key={keyName} onClick={() => setActiveTab(keyName)}>
                <h3>{keyName}</h3>
              </Tab>
            ))}
          </Tabs>
        </TabsWrapper>
        <Content>
          <ImageWrapper>
            <Shadow />
            <img src={DEPARTMENTS[activeTab].src} alt={DEPARTMENTS[activeTab].name} />
          </ImageWrapper>
          <Description>{DEPARTMENTS[activeTab].description}</Description>
        </Content>
      </Wrapper>
    </>
  );
}

const TabsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin: 4rem 0;
  height: 12rem;
  max-height: 12rem;

  @media ${({ theme }) => theme.DEVICE.LG} {
    flex-direction: row;
  }
`;

const ActiveTab = styled.h2`
  font-size: 2.2rem;
  line-height: 121%;
  font-weight: 600;
  background: linear-gradient(91deg, #fc8000 6.41%, #ffb800 85.6%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media ${({ theme }) => theme.DEVICE.XL} {
    font-size: 4.6rem;
    line-height: 121%;
  }
`;

const Tabs = styled.div`
  display: flex;
  justify-content: start;
  gap: 2rem;
  max-width: 100%;
  overflow-x: auto;
  ${scrollBar};

  @media ${({ theme }) => theme.DEVICE.XL} {
    justify-content: end;
  }
`;

type TabProps = {
  isActive: boolean;
};

const Tab = styled.button<TabProps>`
  color: #fff;
  font-size: 1.3rem;
  line-height: 186%;
  background: #111;
  border: none;
  cursor: pointer;
  padding: 0.8rem 1.6rem;
  width: min-content;
  border-radius: 0.4rem;

  ${({ isActive }) =>
    isActive &&
    css`
      border-radius: 3.207px;
      border: 1.464px solid var(--linear, #fc8000);
    `}

  @media ${({ theme }) => theme.DEVICE.XL} {
    font-size: 1.3rem;
    line-height: 184%;
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.BREAKPOINT.LG}) {
    max-width: 658px;
    margin: 0 auto;
  }

  @media ${({ theme }) => theme.DEVICE.LG} {
    grid-template-columns: 1fr 1fr;
    height: 50rem;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  max-width: 100%;
  display: flex;
  align-items: end;
  background: linear-gradient(180deg, #fc8000 0%, rgba(0, 0, 0, 0) 100%);

  img {
    width: 100%;
    height: auto;
  }
`;

const Shadow = styled.div`
  background: linear-gradient(
    360deg,
    rgba(0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 5%,
    rgba(0, 37, 44, 0) 45%,
    rgba(0, 212, 255, 0) 100%
  );
  position: absolute;
  bottom: 0;
  height: 100%;
  width: 100%;
`;

const Description = styled.div`
  font-size: 2rem;
  font-weight: 400;
  line-height: 125%;
  background-color: #111111;
  padding: 3.5rem;
  overflow-y: auto;

  @media ${({ theme }) => theme.DEVICE.XL} {
    font-size: 2.2rem;
    line-height: 130%;
    padding: 5rem;
  }
  p {
    color: #fff;
    margin-bottom: 1.5rem;
  }
  span {
    color: #fc8000;
  }
`;

export default Departments;
